import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Pencil, Clock, AlertTriangle, FlaskConical, Loader2, RotateCw, History, HelpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import ActionEditModal from '../components/BDApprovals/ActionEditModal';
import ActionRejectModal from '../components/BDApprovals/ActionRejectModal';
import TrustLevelPanel from '../components/BDApprovals/TrustLevelPanel';
import AuditHistoryPanel from '../components/BDApprovals/AuditHistoryPanel';

export interface BDAction {
  id: number;
  source_type: string;
  source_id: string;
  action_type: string;
  target: Record<string, any>;
  payload: Record<string, any>;
  diff: { before?: any; after?: any } | null;
  evidence: { quote: string; link?: string | null }[];
  confidence: string | null;
  status: string;
  expires_at: string | null;
  conflict_detail: Record<string, any> | null;
  created_at: string;
}

interface Group {
  sourceType: string;
  sourceId: string;
  items: BDAction[];
  // BD-09: what the meeting's Zoho update and follow-up could not determine. Shown, not approved.
  questions?: string[];
}

const ACTION_LABELS: Record<string, string> = {
  zoho_note: 'Add Zoho note',
  zoho_field_update: 'Update Zoho field',
  zoho_contact_create: 'Create Zoho contact',
  email_draft: 'Draft email',
  email_send: 'Send email',
  pm_task: 'Create PM task',
  pm_comment: 'Add PM comment',
  bd_person_merge: 'Merge duplicate contacts',
  bd_org_merge: 'Merge duplicate organisations',
  bd_skip_rule: 'Skip meetings with this title',
};

/**
 * A readable heading for a group. BD-02's identity proposals use technical
 * source ids ("contact:<email>", "merge:6,7", "org-merge:5,6"); everything else
 * keeps its own source id.
 */
function groupTitle(group: Group): string {
  // BD-13: one owner's changes from a pipeline review ("pipeline:<review>:<user>").
  if (group.sourceType === 'pipeline') {
    const t = group.items.find((i) => i.target?.review_date)?.target;
    return `Pipeline review · ${t?.review_date || ''}${t?.owner_name ? ` · ${t.owner_name}` : ''}`;
  }
  // BD-12: a reply waiting on the person in a watched email thread.
  if (group.sourceType === 'mail_thread') {
    const subject = group.items.find((i) => i.target?.thread_subject)?.target?.thread_subject;
    return `Email thread · ${subject || '(no subject)'}`;
  }
  // BD-09: one meeting's Zoho update; sourceId is the meeting id.
  if (group.sourceType === 'meeting') {
    const title = group.items.find((i) => i.target?.meeting_title)?.target?.meeting_title;
    return title ? `Meeting · ${title}` : `Meeting #${group.sourceId}`;
  }
  // BD-05 calendar learning: "title-rule:<key>" / "sensitivity-rule:<personal|confidential>".
  if (group.sourceType === 'bd_calendar') {
    const label = group.items[0]?.payload?.label;
    if (group.sourceId.startsWith('sensitivity-rule:')) return `Meeting skip rule · all ${label || group.sourceId.slice('sensitivity-rule:'.length)} meetings`;
    if (group.sourceId.startsWith('title-rule:')) return `Meeting skip rule · "${label || group.sourceId.slice('title-rule:'.length)}"`;
    return group.sourceId;
  }
  if (group.sourceType !== 'bd_identity') return group.sourceId;
  const first = group.items[0];
  const id = group.sourceId;
  if (id.startsWith('contact:')) return `New contact · ${id.slice('contact:'.length)}`;
  if (id.startsWith('merge:')) return `Duplicate contacts · ${first?.payload?.shared_email || id.slice('merge:'.length)}`;
  if (id.startsWith('org-merge:')) return `Duplicate organisations · ${first?.payload?.shared_domain || id.slice('org-merge:'.length)}`;
  return id;
}

const MODULE_LABELS: Record<string, string> = { Contacts: 'Contact', Deals: 'Deal', Accounts: 'Account', Leads: 'Lead' };

/** "on Deal · Acme AI · Stage" — which Zoho record (and field) a Zoho action touches. */
function zohoTargetLine(item: BDAction): string | null {
  if (!item.action_type.startsWith('zoho_')) return null;
  const t = item.target || {};
  const mod = MODULE_LABELS[t.module] || t.module;
  if (!mod || mod === 'test') return null;
  const parts = [item.action_type === 'zoho_contact_create' ? `new ${mod}` : `on ${mod}`];
  if (t.label) parts.push(String(t.label));
  if (item.action_type === 'zoho_field_update' && item.payload?.field) parts.push(String(item.payload.field));
  return parts.join(' · ');
}

type Recipient = { email: string; name?: string | null };
const recipientList = (list: unknown): string =>
  (Array.isArray(list) ? list : [])
    .map((r: Recipient) => (r && r.name ? `${r.name} <${r.email}>` : r?.email))
    .filter(Boolean)
    .join(', ');

/**
 * BD-11: what a Jarvis PM item will add, so it is approved knowing what it says —
 * a note: the task it goes on and its text; a task: its title, due date, assignee.
 */
export const PmPreview: React.FC<{ item: BDAction }> = ({ item }) => {
  const p = item.payload || {};
  if (item.action_type === 'pm_comment') {
    return (
      <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid={`bd-pm-preview-${item.id}`}>
        <div className="text-xs text-gray-500 mb-1">
          <span className="font-medium text-gray-600">Note on:</span> {item.target?.label || `task #${p.task_id}`}
        </div>
        <div className="whitespace-pre-wrap text-gray-700">{typeof p.content === 'string' ? p.content : ''}</div>
      </div>
    );
  }
  if (item.action_type === 'pm_task') {
    return (
      <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid={`bd-pm-preview-${item.id}`}>
        <div className="text-gray-800">{p.title || '—'}</div>
        <div className="text-xs text-gray-500 mt-0.5">
          {p.due_date ? `Due ${p.due_date}` : 'No due date'} · assigned to you · in the Business Development project
        </div>
      </div>
    );
  }
  return null;
};

/** BD-10: the follow-up email as it will land in Outlook — recipients, subject, body. */
interface OfferLine { text: string; anchors?: string[]; item?: { title?: string; why?: string; url?: string | null } }

/**
 * BD-10: the optional case-study line (BD-17) is a suggestion the person turns ON; it is never in the
 * email until they add it. Same rule as backend expertiseLine.insertLine: before the close, else before
 * the sign-off, else at the end.
 */
export function insertOfferLine(body: string, offer: OfferLine): string {
  if (!offer?.text || body.includes(offer.text)) return body;
  for (const anchor of offer.anchors || []) {
    const at = anchor ? body.lastIndexOf(anchor) : -1;
    if (at > 0) return `${body.slice(0, at)}${offer.text}\n\n${body.slice(at)}`;
  }
  return `${body.replace(/\s+$/, '')}\n\n${offer.text}`;
}

export const EmailDraftPreview: React.FC<{ item: BDAction; onChanged?: () => void }> = ({ item, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const p = item.payload || {};
  const body: string = typeof p.body === 'string' ? p.body : '';
  const offer: OfferLine | null = p.offer && typeof p.offer.text === 'string' ? p.offer : null;
  const offerAdded = Boolean(offer && body.includes(offer.text));
  const addOffer = async () => {
    if (!offer || adding) return;
    setAdding(true);
    try {
      await api.post(`/api/bd-approvals/${item.id}/edit`, { editedPayload: { ...p, body: insertOfferLine(body, offer) } });
      toast.success('Line added to the draft');
      onChanged?.();
    } catch {
      toast.error('Could not add the line');
    } finally {
      setAdding(false);
    }
  };
  const lines = body.split('\n');
  const long = lines.length > 8;
  // BD-12 reply drafts carry the message being answered (the thread context).
  const inbound = item.target?.kind === 'reply' ? item.target?.inbound : null;
  // BD-10: a follow-up that will be made as a reply in the meeting's email thread.
  const meetingThread: { thread_id: number; subject: string | null } | null = item.target?.kind !== 'reply' ? item.target?.reply_to || null : null;
  return (
    <>
    {inbound?.excerpt && (
      <details className="mt-2 rounded border border-blue-100 bg-blue-50 px-3 py-2 text-sm" data-testid={`bd-reply-context-${item.id}`} open>
        <summary className="cursor-pointer text-xs font-medium text-blue-800">
          Their message{inbound.from?.name || inbound.from?.email ? ` from ${inbound.from.name || inbound.from.email}` : ''}
          {inbound.at ? ` · ${new Date(inbound.at).toLocaleString()}` : ''}
        </summary>
        <div className="mt-1 whitespace-pre-wrap text-gray-700">{inbound.excerpt}</div>
      </details>
    )}
    <div className="mt-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm" data-testid={`bd-email-preview-${item.id}`}>
      <div className="text-xs text-gray-500"><span className="font-medium text-gray-600">To:</span> {recipientList(p.to) || '—'}</div>
      {recipientList(p.cc) && (
        <div className="text-xs text-gray-500"><span className="font-medium text-gray-600">Cc:</span> {recipientList(p.cc)}</div>
      )}
      <div className="text-xs text-gray-500 mb-1">
        <span className="font-medium text-gray-600">Subject:</span>{' '}
        {meetingThread
          ? (/^re:/i.test(meetingThread.subject || '') ? meetingThread.subject : `RE: ${meetingThread.subject || '(the thread)'}`)
          : (p.subject || '—')}
      </div>
      <div className="whitespace-pre-wrap text-gray-700">{open || !long ? body : `${lines.slice(0, 8).join('\n')}\n…`}</div>
      {long && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="mt-1 text-xs text-blue-600 hover:underline">
          {open ? 'Show less' : 'Show full email'}
        </button>
      )}
      <div className="mt-1 text-xs text-gray-400">
        {item.target?.kind === 'reply'
          ? 'Approving creates this as a reply draft inside the thread in Outlook. Nothing is sent.'
          : meetingThread
            ? `Approving creates this as a reply in your email thread "${meetingThread.subject || 'with them'}" in Outlook, to the meeting's people only. Nothing is sent.`
            : 'Approving creates this as a draft in Outlook. Nothing is sent.'}
      </div>
    </div>
    {offer && !offerAdded && (
      <div className="mt-2 rounded border border-dashed border-gray-300 px-3 py-2 text-sm" data-testid={`bd-offer-${item.id}`}>
        <div className="text-xs font-medium text-gray-600">Suggested line (optional): not in the email unless you add it</div>
        <div className="mt-1 text-gray-700">{offer.text}</div>
        {offer.item?.why && (
          <div className="mt-0.5 text-xs text-gray-500">
            {offer.item.why}
            {offer.item.url && <> · <a href={offer.item.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Case study</a></>}
          </div>
        )}
        <button type="button" onClick={addOffer} disabled={adding} className="mt-1 text-xs font-medium text-blue-600 hover:underline disabled:opacity-50" data-testid={`bd-offer-add-${item.id}`}>
          {adding ? 'Adding…' : 'Add to draft'}
        </button>
      </div>
    )}
    </>
  );
};

function timeUntil(iso: string | null): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days >= 1) return `expires in ${days}d`;
  const hours = Math.floor(ms / (60 * 60 * 1000));
  return `expires in ${Math.max(hours, 1)}h`;
}

const BDApprovals: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<{ id: number; action: 'approve' | 'retry' } | null>(null);
  const [busySourceId, setBusySourceId] = useState<string | null>(null);
  const [editingAction, setEditingAction] = useState<BDAction | null>(null);
  const [rejectingAction, setRejectingAction] = useState<BDAction | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showTrustLevels, setShowTrustLevels] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/bd-approvals');
      setGroups(res.data?.groups || []);
    } catch (err) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id: number) => {
    setBusy({ id, action: 'approve' });
    try {
      await api.post(`/api/bd-approvals/${id}/approve`);
      toast.success('Approved');
      await load();
    } catch (err) {
      toast.error('Failed to approve');
    } finally {
      setBusy(null);
    }
  };

  const approveAll = async (sourceType: string, sourceId: string) => {
    setBusySourceId(sourceId);
    try {
      const res = await api.post('/api/bd-approvals/approve-all', { sourceType, sourceId });
      toast.success(`Approved ${res.data?.results?.length || 0} item(s)`);
      await load();
    } catch (err) {
      toast.error('Failed to approve all');
    } finally {
      setBusySourceId(null);
    }
  };

  // Rejecting opens ActionRejectModal (optional reason); the modal makes the call.
  const onRejected = async () => {
    setRejectingAction(null);
    await load();
  };

  const retry = async (id: number) => {
    setBusy({ id, action: 'retry' });
    try {
      const res = await api.post(`/api/bd-approvals/${id}/retry`);
      if (res.data?.ok === false) {
        toast.error(`Still failing: ${res.data.reason || 'unknown error'}`);
      } else {
        toast.success('Completed');
      }
      await load();
    } catch (err) {
      toast.error('Retry failed');
    } finally {
      setBusy(null);
    }
  };

  const onEditSaved = async () => {
    setEditingAction(null);
    await load();
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading approvals…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
          <p className="text-sm text-gray-500 mt-1">
            Nothing Jarvis's BD assistant proposes is written anywhere without your yes here.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50 inline-flex items-center gap-1.5"
            >
              <History className="w-4 h-4" /> {showHistory ? 'Hide history' : 'History'}
            </button>
            <button
              onClick={() => setShowTrustLevels((v) => !v)}
              className="text-sm px-3 py-1.5 rounded border text-gray-600 hover:bg-gray-50"
            >
              {showTrustLevels ? 'Hide trust levels' : 'Trust levels'}
            </button>
          </div>
        )}
      </div>

      {showTrustLevels && isAdmin && <TrustLevelPanel />}
      {showHistory && isAdmin && <AuditHistoryPanel />}

      {groups.length === 0 && (
        <div className="text-center py-16 text-gray-400 border border-dashed rounded-lg">
          Nothing waiting on you right now.
        </div>
      )}

      {groups.map((group) => (
        <div key={`${group.sourceType}:${group.sourceId}`} className="mb-6 border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between bg-gray-50 px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              {group.sourceType === 'synthetic' && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                  <FlaskConical className="w-3 h-3" /> TEST DATA
                </span>
              )}
              <span className="text-sm font-medium text-gray-700" title={group.sourceId}>{groupTitle(group)}</span>
              <span className="text-xs text-gray-400">({group.items.length} item{group.items.length === 1 ? '' : 's'})</span>
            </div>
            <button
              onClick={() => approveAll(group.sourceType, group.sourceId)}
              disabled={busySourceId === group.sourceId}
              className="text-sm px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {busySourceId === group.sourceId && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Approve all for this source
            </button>
          </div>

          {(group.questions || []).length > 0 && (
            <div className="px-4 py-3 border-b bg-blue-50/50" data-testid={`bd-approval-questions-${group.sourceId}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-800">
                <HelpCircle className="w-3.5 h-3.5" /> Questions Jarvis could not answer from this meeting
              </div>
              <ul className="mt-1 space-y-0.5 text-sm text-gray-700 list-disc pl-5">
                {(group.questions || []).map((q) => <li key={q}>{q}</li>)}
              </ul>
              <p className="mt-1 text-xs text-gray-500">Nothing to approve here: these are for you to decide (in Zoho, Jarvis PM or by editing the proposals below). Jarvis never guesses these.</p>
            </div>
          )}

          <div className="divide-y">
            {group.items.map((item) => (
              <div key={item.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{ACTION_LABELS[item.action_type] || item.action_type}</span>
                      {item.status === 'conflict' && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded">
                          <AlertTriangle className="w-3 h-3" /> conflict — live value changed since this was proposed
                        </span>
                      )}
                      {item.status === 'approved' && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                          <AlertTriangle className="w-3 h-3" /> approved, but the write failed — retry
                        </span>
                      )}
                      {item.confidence && (
                        <span className="text-xs text-gray-400">confidence: {item.confidence}</span>
                      )}
                    </div>
                    {zohoTargetLine(item) && (
                      <div className="text-xs text-gray-500 mt-0.5" data-testid={`bd-target-${item.id}`}>{zohoTargetLine(item)}</div>
                    )}
                    {item.action_type === 'email_draft' && <EmailDraftPreview item={item} onChanged={load} />}
                    {(item.action_type === 'pm_comment' || item.action_type === 'pm_task') && <PmPreview item={item} />}
                    {item.diff?.after != null && (
                      <div className="text-sm text-gray-600 mt-1">
                        {item.diff.before != null && (
                          <span className="line-through text-gray-400 mr-2">{String(item.diff.before)}</span>
                        )}
                        <span>{String(item.diff.after)}</span>
                      </div>
                    )}
                    {item.evidence?.[0]?.quote && (
                      <div className="text-xs text-gray-400 mt-1 italic">"{item.evidence[0].quote}"</div>
                    )}
                    {item.expires_at && (
                      <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {timeUntil(item.expires_at)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.status === 'approved' ? (
                      // approve() always executes immediately — a row only
                      // sits here if that execute() genuinely failed.
                      <button
                        onClick={() => retry(item.id)}
                        disabled={busy?.id === item.id}
                        className="p-2 rounded hover:bg-amber-50 text-amber-600 disabled:opacity-50"
                        title="Retry — the write failed after approval"
                      >
                        {busy?.id === item.id && busy.action === 'retry' ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <RotateCw className="w-5 h-5" />
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => approve(item.id)}
                        disabled={busy?.id === item.id || item.status === 'conflict'}
                        className="p-2 rounded hover:bg-green-50 text-green-600 disabled:opacity-50"
                        title={item.status === 'conflict' ? 'Resolve the conflict with Edit or Reject first' : 'Approve'}
                      >
                        {busy?.id === item.id && busy.action === 'approve' ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <CheckCircle className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setEditingAction(item)}
                      disabled={busy?.id === item.id}
                      className="p-2 rounded hover:bg-blue-50 text-blue-600 disabled:opacity-50"
                      title="Edit"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setRejectingAction(item)}
                      disabled={busy?.id === item.id}
                      className="p-2 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                      title="Reject"
                      data-testid={`bd-reject-${item.id}`}
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editingAction && (
        <ActionEditModal
          action={editingAction}
          onClose={() => setEditingAction(null)}
          onSaved={onEditSaved}
        />
      )}

      {rejectingAction && (
        <ActionRejectModal
          action={rejectingAction}
          label={ACTION_LABELS[rejectingAction.action_type] || rejectingAction.action_type}
          onClose={() => setRejectingAction(null)}
          onRejected={onRejected}
        />
      )}
    </div>
  );
};

export default BDApprovals;
